import type { Difficulty, FillQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-3-2：進位加法 + 退位減法直式（三上版）。
 * 三上第二章：含多次進退位的直式計算。
 * 與三下的 math.calc.addsub-vertical / math.calc.addsub-vertical-sub 共用邏輯，
 * 但 kp id 不同以便三上獨立統計。
 *
 * difficulty 1：三位數 ± 三位數（單次進退位）
 * difficulty 2：三位數 ± 三位數（多次進退位）
 * difficulty 3：四位數 ± 三/四位數（多次進退位）
 *
 * 每次呼叫隨機選加或減（由 seed 決定）。
 */
export function generateAddSubVerticalUp(seed: number, difficulty: Difficulty): FillQuestion {
  const rand = mulberry32(seed);
  const isAdd = rand() > 0.5; // deterministic from seed

  let a: number;
  let b: number;

  if (difficulty === 1) {
    if (isAdd) {
      // Single carry: ones sum ≥ 10, tens sum < 10, result < 1000
      do {
        const oA = randInt(rand, 5, 9);
        const oB = randInt(rand, 10 - oA, 9);
        const tA = randInt(rand, 1, 4);
        const tB = randInt(rand, 1, 4);
        const hA = randInt(rand, 1, 4);
        const hB = randInt(rand, 1, 4);
        a = hA * 100 + tA * 10 + oA;
        b = hB * 100 + tB * 10 + oB;
      } while (a + b >= 1000);
    } else {
      // Single borrow: ones of a < ones of b
      do {
        const oB = randInt(rand, 5, 9);
        const oA = randInt(rand, 1, oB - 1);
        const tA = randInt(rand, 5, 9);
        const tB = randInt(rand, 1, tA);
        const hA = randInt(rand, 2, 9);
        const hB = randInt(rand, 1, hA - 1);
        a = hA * 100 + tA * 10 + oA;
        b = hB * 100 + tB * 10 + oB;
      } while (a <= b);
    }
  } else if (difficulty === 2) {
    if (isAdd) {
      // Multiple carries: ones/tens/hundreds 全進位(建構式保證,迴圈只當保險)。
      // ⚠ 2026-06-11 根治死迴圈:原本 hund 取 1..4,兩數和最大 998,卻要求
      // a+b ≥ 1000 → 條件永遠不成立(與 math/add-carry.ts 同款複製貼上 bug)。
      do {
        const oA = randInt(rand, 6, 9);
        const oB = randInt(rand, 10 - oA, 9);
        const tA = randInt(rand, 5, 9);
        const tB = randInt(rand, 10 - tA, 9);
        const hA = randInt(rand, 5, 9);
        const hB = randInt(rand, 10 - hA, 9);
        a = hA * 100 + tA * 10 + oA;
        b = hB * 100 + tB * 10 + oB;
      } while (a + b < 1000 || a + b > 9999);
    } else {
      // Multiple borrows
      do {
        const oB = randInt(rand, 6, 9);
        const oA = randInt(rand, 0, oB - 1);
        const tB = randInt(rand, 5, 9);
        const tA = randInt(rand, 0, tB - 1);
        const hA = randInt(rand, 3, 9);
        const hB = randInt(rand, 1, hA - 1);
        a = hA * 100 + tA * 10 + oA;
        b = hB * 100 + tB * 10 + oB;
      } while (a <= b);
    }
  } else {
    // d3: four-digit
    if (isAdd) {
      a = randInt(rand, 1000, 4999);
      b = randInt(rand, 1000, 4999);
    } else {
      do {
        a = randInt(rand, 2000, 9999);
        b = randInt(rand, 1000, a - 1);
      } while (a - b < 100);
    }
  }

  const correct = isAdd ? a + b : a - b;
  const op = isAdd ? '+' : '−';
  const kp = isAdd ? 'math.add.carry.multi' : 'math.sub.borrow.multi';

  return {
    kind: 'fill',
    prompt: `${a} ${op} ${b} = ?`,
    correctAnswer: String(correct),
    acceptedAnswers: [String(correct)],
    explanation: `直式計算：${a} ${op} ${b} = ${correct}`,
    kp,
  };
}
